import { Component, Input, OnInit, ElementRef, ViewChild, Output, EventEmitter, HostListener } from '@angular/core';

@Component({
  selector: 'tog-autocomplete',
  templateUrl: 'autocomplete.template.html',
  styleUrls: ['autocomplete.scss'],
})
export class AutocompleteComponent implements OnInit {
  filterText: string = ''; // user input
  filteredElements: any[] = [];
  hideResults: boolean = false;
  noResults: boolean = false;
  isAboveMidline: boolean = true;

  keySelection: number = 0;

  @Input() clearOnSelect: Boolean = true;

  // (optional)
  // the component should figure out where to show
  // the results on its own, but if you're getting wonkiness
  // or want to otherwise force an orientation, pass one of these in
  @Input() forceAbove: boolean = false;
  @Input() forceBelow: boolean = false;

  // elements to search through and
  // (optional) keys to search by
  @Input() searchElements: any[];
  @Input() keysToSearch: string[] = [];

  // allows parent to determine which key on the
  // search elements to use to display results
  @Input() displayResultsBy: string = '';

  // set whether to show all results in the dropdown
  // and if not, how many results to show (default 5)
  @Input() allResultsOnDropdown: boolean = false;
  @Input() numResultsToShow: number = 5;

  // (optional)
  @Input() placeholder: string = 'Search';
  @Input() noResultsText: string = 'No results.';
  @Input() customRightIcon: string = '';
  @Input() showClearInputButton: boolean = false;


  @Input() set initialText(name: string) {
    this.filterText = name;
  }

  // lets parents programmatically clear the field
  @Input() set clear(clear: boolean) {
    if (clear === true) {
      this.filterText = '';
    }
  }

  @Output('select-change') change = new EventEmitter();
  @Output('blur') blur = new EventEmitter();
  @Output('delete') delete = new EventEmitter();

  @ViewChild('container') container: ElementRef;
  @ViewChild('leftIcon') leftIcon: ElementRef;
  @ViewChild('resultList') resultList: ElementRef;

  constructor(private _elementRef: ElementRef) {
  }

  ngOnInit() {
    if (!this.forceAbove && !this.forceBelow) {
      this.checkPageHeight();
    } else if (this.forceAbove) {
      this.isAboveMidline = false;
    } else {
      this.isAboveMidline = true;
    }
  }

  @HostListener('document:click', ['$event'])
  dismiss(e) {
    let clickedInside = this._elementRef.nativeElement.contains(e.target);
    if (!clickedInside) this.hideResults = true;
    this.keySelection = 0;
  }

  selectElement(el: any) {
    this.change.emit(el);
    if (this.clearOnSelect) {
      this.filterText = '';
      this.filteredElements = [];
    }
    else {
      this.filterText = el.name || el[this.displayResultsBy];
    }
    this.hideResults = true;
  }

  @HostListener('document:keydown', ['$event'])
  keyHandler(keypress: any) {
    if (!this.hideResultList()) {
      switch (keypress.keyCode) {
        case 38: // up arrow
          keypress.preventDefault();
          this.keySelection = Math.max(0, this.keySelection - 1);
          this.scrollList();
          break;

        case 40: // down arrow
          keypress.preventDefault();
          this.keySelection = Math.min(this.filteredElements.length - 1,
                                       this.keySelection + 1);
          this.scrollList();
          break;

        case 13: // enter key
          keypress.preventDefault();
          let result = this.filteredElements[this.keySelection];
          if (result) this.selectElement(result);
          break;
      }
    }
  }

  // handles scrolling the list with arrow keys
  scrollList() {
    let maxHeight = 300; // from .scss
    let list = this.resultList.nativeElement;
    if (list.children[this.keySelection]) {
      let selectedItem = list.children[this.keySelection];
      let itemHeight = selectedItem.scrollHeight;
      let itemPosition = this.keySelection * itemHeight;

      if ((itemPosition + itemHeight) > maxHeight) {
        list.scrollTop += itemHeight;
      }
      if (itemPosition < list.scrollTop) {
        list.scrollTop -= itemHeight;
      }
    }
  }

  updateFilterText() {
    this.blur.emit(this.filterText);
  }

  updateResults(e: any) {
    this.noResults = false;
    this.filteredElements = [];
    let filterText = (e && 'filter' in e) ? e.filter.toLowerCase() : '';
    // if no search terms, show all results if that option is set
    if (!filterText) {
      this.mightDropDown();
      return;
    }
    if (this.searchElements) {
      this.searchElements.forEach(obj => {
        let keys = this.keysToSearch.length ? this.keysToSearch : Object.keys(obj); // use provided keys, or find obj keys
        for (let i = 0; i < keys.length; i++) {
          let key = keys[i];
          if (String(obj[key]).toLowerCase().indexOf(filterText) > -1) {
            this.filteredElements.push(obj);
            this.hideResults = false;
            break;
          }
        }
      });
    }
    if (!this.filteredElements.length) this.noResults = true;
  }

  checkPageHeight() {
    let placeOnPage = this.container.nativeElement.parentNode.getBoundingClientRect().top;
    let pageThreshold = window.innerHeight / 2;
    placeOnPage < pageThreshold ? this.isAboveMidline = true : this.isAboveMidline = false;
  }

  determineResultListOffset() {
    let numResults = (this.filteredElements && this.filteredElements.length) ? this.filteredElements.length : 1;
    let listItemHeight = 31; // height of each list itemHeight
    let offset = this.isAboveMidline ? '0' : Math.min(334, (numResults * listItemHeight) + 36) + 'px';
    return offset;
  }

  // searches for any <i> tags within ng-content and
  // checks to see if any of them are currently visible
  checkForLeftIcon() {
    let nodes = this.leftIcon.nativeElement.childNodes;
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].tagName === 'I' && nodes[i].clientHeight !== 0) {
        return true; // as soon as one icon is visible we're done here
      }
    }
    return false;
  }

  mightDropDown() {
    if (this.allResultsOnDropdown && this.filterText === '') {
      this.filteredElements = this.searchElements;
    }
  }

  deleteFilterText() {
    this.filterText = '';
    this.noResults = false;
    this.delete.emit('');
  }

  // These handle the logic that was originally in the
  // template as this makes it a little easier to read
  showSearchIcon() {
    return (!this.allResultsOnDropdown && this.showClearInputButton && this.filterText && !this.filterText.length) ||
           (!this.allResultsOnDropdown && !this.showClearInputButton) && !this.customRightIcon;
  }

  showDropdownCaret() {
    return this.allResultsOnDropdown && !this.customRightIcon;
  }

  showClearIcon() {
    return this.showClearInputButton && this.filterText && this.filterText.length > 0 && !this.customRightIcon;
  }

  hideResultList() {
    return this.hideResults ||
          (this.filteredElements && !this.filteredElements.length && !this.noResults) ||
          (!this.filterText && !this.allResultsOnDropdown);
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/autocomplete/autocomplete.component.ts